import React from 'react'
import { Typography } from '@mui/material'

export const DEFAULT_PRIMARY_COLOR = '#0068EF'
export const DEFAULT_BRAND_NAME = 'AautiPay'

/**
 * Resolves the customisable branding values from the plugin config / props.
 * Every value is optional; anything missing falls back to the AautiPay defaults.
 *
 * @param {Object} config
 * @param {string} [config.primaryColor]    - Accent color (buttons, links, fallbacks below)
 * @param {string} [config.themeColor]      - Background color of the page body
 * @param {string} [config.headerText]      - Text shown in the header (default 'AautiPay')
 * @param {string} [config.headerColor]     - Header background (default primaryColor)
 * @param {string} [config.headerTextColor] - Header text color (default '#FFFFFF')
 * @param {string} [config.loaderColor]     - Loader spinner color (default primaryColor)
 */
export const getBranding = (config = {}) => {
  const primaryColor = toHexColor(config?.primaryColor) || DEFAULT_PRIMARY_COLOR
  return {
    primaryColor,
    themeColor: toHexColor(config?.themeColor) || '#FFFFFF',
    headerText: config?.headerText || DEFAULT_BRAND_NAME,
    headerColor: toHexColor(config?.headerColor) || primaryColor,
    headerTextColor: toHexColor(config?.headerTextColor) || '#FFFFFF',
    loaderColor: toHexColor(config?.loaderColor) || primaryColor,
  }
}

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

/**
 * Returns the colour as 6-digit uppercase hex ('#abc' -> '#AABBCC'), or '' when
 * it isn't a plain hex colour. Colours from props / iframe data are not checked
 * by the backend, and they are appended with alpha digits (`${color}80`) and
 * written into the bank-redirect popup's HTML, so nothing else may get through.
 */
const toHexColor = (value) => {
  if (typeof value !== 'string') return ''
  const hex = value.trim().toUpperCase()
  if (!HEX_COLOR_REGEX.test(hex)) return ''
  return hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex
}

export const BRANDING_KEYS = ['primaryColor', 'themeColor', 'headerText', 'headerColor', 'headerTextColor', 'loaderColor']

/**
 * Applies the branding saved for the app in the AautiPay portal (returned by
 * getIframe / get-payment-link / login as `branding`) over the props / staticData.
 * Empty values are skipped, so they fall back to the props and then the defaults.
 *
 * @param {Object} config - props / staticData
 * @param {Object} [branding] - branding from the backend
 */
export const applyBackendBranding = (config = {}, branding) => {
  if (!branding || typeof branding !== 'object') return config
  const merged = { ...config }
  BRANDING_KEYS.forEach((key) => {
    if (branding[key]) merged[key] = branding[key]
  })
  return merged
}

/** Fixed top header bar. */
export const BrandHeader = ({ text, color, textColor, fontWeight = 600 }) => (
  <div
    style={{
      padding: '12px 20px',
      backgroundColor: color || DEFAULT_PRIMARY_COLOR,
      flexShrink: 0,
    }}
  >
    <Typography style={{ color: textColor || '#fff', fontWeight, fontSize: 18, letterSpacing: '-0.3px' }}>
      {text || DEFAULT_BRAND_NAME}
    </Typography>
  </div>
)

/** "🔒 Powered by AautiPay" footer line — the brand name here is fixed. */
export const PoweredBy = ({ color, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 16, flexShrink: 0, ...style }}>
    <svg width='12' height='12' viewBox='0 0 24 24' fill='none'>
      <rect x='3' y='11' width='18' height='11' rx='2' stroke='#8a96a8' strokeWidth='2' />
      <path d='M7 11V7a5 5 0 0 1 10 0v4' stroke='#8a96a8' strokeWidth='2' strokeLinecap='round' />
    </svg>
    <Typography component='span' style={{ color: '#8a96a8', fontSize: 11 }}>
      Powered by{' '}
    </Typography>
    <Typography component='span' style={{ fontWeight: 500, fontSize: 11, color: color || DEFAULT_PRIMARY_COLOR }}>
      {DEFAULT_BRAND_NAME}
    </Typography>
  </div>
)
